import { useState, useEffect, useCallback, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MapPin, Search, X } from "lucide-react";
import { toast } from "sonner";
import { useCampaign } from "@/hooks/use-campaign";
import { useSession } from "@/hooks/use-session";
import { useRole } from "@/hooks/use-role";
import { apiService } from "@/lib/api-service";
import {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
	CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { RoleBadge } from "@/components/team/RoleBadge";
import { ROLE_DESCRIPTIONS, ROLE_CAPABILITIES } from "@/types/team";
import type { GeocodeResponse } from "@/types/geocode";

interface PlaceSuggestion {
	placePrediction: {
		placeId: string;
		text: { text: string };
		structuredFormat?: {
			mainText: { text: string };
			secondaryText: { text: string };
		};
	};
}

interface AutocompleteResponse {
	suggestions: PlaceSuggestion[];
}

interface AddressData {
	street: string;
	city: string;
	state: string;
	zip_code: string;
}

export const Route = createFileRoute("/_app/_settings/settings")({
	component: SettingsPage,
});

/** Convert cents (integer) to dollar string for display. Returns "" for null/undefined. */
function centsToDollars(cents: number | null | undefined): string {
	if (cents == null) return "";
	return (cents / 100).toString();
}

/** Convert dollar string to cents (integer). Returns null for empty/invalid input. */
function dollarsToCents(dollars: string): number | null {
	if (!dollars.trim()) return null;
	const parsed = parseFloat(dollars);
	if (Number.isNaN(parsed)) return null;
	return Math.round(parsed * 100);
}

/** Convert a string to an integer, or null if empty/invalid. */
function toIntOrNull(value: string): number | null {
	if (!value.trim()) return null;
	const parsed = parseInt(value, 10);
	if (Number.isNaN(parsed)) return null;
	return parsed;
}

function SettingsPage() {
	return (
		<div className="space-y-8 px-6 py-6">
			<YourAccessSection />
			<BillingAddressSection />
			<CampaignGoalsSection />
		</div>
	);
}

function YourAccessSection() {
	const { role, membership } = useRole();

	if (!membership) return null;

	const caps = ROLE_CAPABILITIES[role] ?? ROLE_CAPABILITIES["readonly"];
	const capLabels: { key: keyof typeof caps; label: string }[] = [
		{ key: "view_dashboard", label: "View dashboard" },
		{ key: "view_content", label: "View campaign content" },
		{ key: "create_content", label: "Create content" },
		{ key: "edit_content", label: "Edit content" },
		{ key: "send_campaigns", label: "Send campaigns" },
		{ key: "import_contacts", label: "Import contacts" },
		{ key: "manage_users", label: "Manage team members" },
		{ key: "manage_settings", label: "Manage campaign settings" },
	];

	return (
		<Card>
			<CardHeader>
				<div className="flex items-center gap-3">
					<CardTitle>Your Access</CardTitle>
					<RoleBadge role={role} />
				</div>
				<CardDescription>{ROLE_DESCRIPTIONS[role]}</CardDescription>
			</CardHeader>
			<CardContent>
				<ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
					{capLabels.map(({ key, label }) => (
						<li key={key} className="flex items-center gap-2 text-sm">
							<span
								className={
									caps[key]
										? "text-green-600 dark:text-green-400"
										: "text-muted-foreground/50"
								}
							>
								{caps[key] ? "✓" : "✗"}
							</span>
							<span className={caps[key] ? "" : "text-muted-foreground/60"}>
								{label}
							</span>
						</li>
					))}
				</ul>
			</CardContent>
		</Card>
	);
}

// ---------------------------------------------------------------------------
// Billing Address Section
// ---------------------------------------------------------------------------

function parseAddressComponents(
	result: GeocodeResponse["results"][number],
): AddressData {
	const get = (type: string) =>
		result.addressComponents.find((c) => c.types.includes(type));
	const streetNumber = get("street_number")?.longText ?? "";
	const route = get("route")?.longText ?? "";
	const city =
		get("locality")?.longText ?? get("sublocality")?.longText ?? "";
	const state = get("administrative_area_level_1")?.shortText ?? "";
	const zip = get("postal_code")?.longText ?? "";
	const street = [streetNumber, route].filter(Boolean).join(" ");
	return { street, city, state, zip_code: zip };
}

function BillingAddressSection() {
	const { isReadWrite } = useRole();
	const [addressInput, setAddressInput] = useState("");
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
	const [parsedAddress, setParsedAddress] = useState<AddressData | null>(null);
	const [geocoding, setGeocoding] = useState(false);
	const [saving, setSaving] = useState(false);
	const suggestionsRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	// Debounced autocomplete
	const [debouncedInput, setDebouncedInput] = useState("");
	useEffect(() => {
		if (!addressInput || addressInput.length < 3 || selectedAddress) {
			setDebouncedInput("");
			return;
		}
		const timer = setTimeout(() => setDebouncedInput(addressInput), 300);
		return () => clearTimeout(timer);
	}, [addressInput, selectedAddress]);

	const { data: suggestions, isFetching: suggestionsFetching } = useQuery({
		queryKey: ["address-autocomplete", debouncedInput],
		queryFn: () =>
			apiService.get<AutocompleteResponse>(
				`/campaigns/autocomplete?input=${encodeURIComponent(debouncedInput)}`,
			),
		enabled: debouncedInput.length >= 3,
		staleTime: 30_000,
	});

	// Close dropdown on outside click
	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (
				suggestionsRef.current &&
				!suggestionsRef.current.contains(e.target as Node) &&
				inputRef.current &&
				!inputRef.current.contains(e.target as Node)
			) {
				setShowSuggestions(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () =>
			document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const handleSelectSuggestion = useCallback(
		async (suggestion: PlaceSuggestion) => {
			const fullText = suggestion.placePrediction.text.text;
			setAddressInput(fullText);
			setSelectedAddress(fullText);
			setShowSuggestions(false);
			setGeocoding(true);

			try {
				const geocodeRes = await apiService.get<GeocodeResponse>(
					`/campaigns/geocode?address=${encodeURIComponent(fullText)}`,
				);
				const result = geocodeRes.results[0];
				if (result) {
					const parsed = parseAddressComponents(result);
					setParsedAddress(parsed);
				}
			} catch {
				toast.error("Failed to geocode address");
				setSelectedAddress(null);
			} finally {
				setGeocoding(false);
			}
		},
		[],
	);

	const handleClearAddress = useCallback(() => {
		setAddressInput("");
		setSelectedAddress(null);
		setParsedAddress(null);
	}, []);

	const handleSave = async () => {
		if (!parsedAddress) return;
		setSaving(true);
		try {
			// TODO: Wire to a user-facing profile update endpoint for billing_address.
			// The admin API supports billing_address on AccountUpdateRequest, but
			// there is no user-facing endpoint yet. For now, show a success toast.
			await new Promise((resolve) => setTimeout(resolve, 300));
			toast.info("Billing address saved locally. Server sync coming soon.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<Card>
			<CardHeader>
				<CardTitle>Billing Address</CardTitle>
				<CardDescription>
					Set your billing address for campaign compliance and
					reporting.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				{selectedAddress && parsedAddress ? (
					<>
						<div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
							<MapPin className="h-4 w-4 shrink-0 text-primary" />
							<span className="flex-1 text-sm">
								{selectedAddress}
							</span>
							<button
								type="button"
								onClick={handleClearAddress}
								className="rounded-full p-1 hover:bg-muted"
							>
								<X className="h-4 w-4 text-muted-foreground" />
							</button>
						</div>
						<div className="grid grid-cols-2 gap-4 text-sm">
							<div className="space-y-1">
								<Label className="text-xs text-muted-foreground">
									Street
								</Label>
								<p>{parsedAddress.street || "--"}</p>
							</div>
							<div className="space-y-1">
								<Label className="text-xs text-muted-foreground">
									City
								</Label>
								<p>{parsedAddress.city || "--"}</p>
							</div>
							<div className="space-y-1">
								<Label className="text-xs text-muted-foreground">
									State
								</Label>
								<p>{parsedAddress.state || "--"}</p>
							</div>
							<div className="space-y-1">
								<Label className="text-xs text-muted-foreground">
									Zip Code
								</Label>
								<p>{parsedAddress.zip_code || "--"}</p>
							</div>
						</div>
					</>
				) : (
					<div className="relative">
						<div className="relative">
							{suggestionsFetching ? (
								<Spinner className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
							) : (
								<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
							)}
							<Input
								ref={inputRef}
								placeholder="Search for an address..."
								value={addressInput}
								onChange={(e) => {
									setAddressInput(e.target.value);
									setShowSuggestions(true);
								}}
								onFocus={() => setShowSuggestions(true)}
								className="pl-9"
							/>
							{geocoding && (
								<div className="absolute right-3 top-1/2 -translate-y-1/2">
									<Spinner />
								</div>
							)}
						</div>

						{showSuggestions &&
							suggestions?.suggestions &&
							suggestions.suggestions.length > 0 && (
								<div
									ref={suggestionsRef}
									className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg"
								>
									{suggestions.suggestions.map((s) => (
										<button
											key={s.placePrediction.placeId}
											type="button"
											className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
											onClick={() =>
												handleSelectSuggestion(s)
											}
										>
											<MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
											<div>
												<div className="font-medium">
													{
														s.placePrediction
															.structuredFormat
															?.mainText.text
													}
												</div>
												<div className="text-xs text-muted-foreground">
													{
														s.placePrediction
															.structuredFormat
															?.secondaryText.text
													}
												</div>
											</div>
										</button>
									))}
								</div>
							)}
					</div>
				)}
			</CardContent>
			{parsedAddress && (
				<CardFooter className="border-t pt-6">
					<Button onClick={handleSave} disabled={saving || !isReadWrite}>
						{saving && (
							<Loader2 className="mr-2 size-4 animate-spin" />
						)}
						{saving ? "Saving..." : "Save Address"}
					</Button>
				</CardFooter>
			)}
		</Card>
	);
}

function CampaignGoalsSection() {
	const { isReadWrite } = useRole();
	const campaign = useCampaign();
	const session = useSession();
	const queryClient = useQueryClient();
	const campaignId = session.campaign?.id;

	const [saving, setSaving] = useState(false);

	// Form state -- dollars for display, empty string for "not set"
	const [fundraisingGoal, setFundraisingGoal] = useState<string>("");
	const [budgetTarget, setBudgetTarget] = useState<string>("");
	const [winNumber, setWinNumber] = useState<string>("");

	// Track initial values to detect dirty state
	const [initialFundraisingGoal, setInitialFundraisingGoal] =
		useState<string>("");
	const [initialBudgetTarget, setInitialBudgetTarget] = useState<string>("");
	const [initialWinNumber, setInitialWinNumber] = useState<string>("");

	// Initialize form state from campaign data
	const initializeForm = useCallback(() => {
		if (campaign) {
			const fg = centsToDollars(campaign.fundraising_goal);
			const bt = centsToDollars(campaign.budget_target);
			const wn =
				campaign.win_number != null
					? campaign.win_number.toString()
					: "";

			setFundraisingGoal(fg);
			setBudgetTarget(bt);
			setWinNumber(wn);
			setInitialFundraisingGoal(fg);
			setInitialBudgetTarget(bt);
			setInitialWinNumber(wn);
		}
	}, [campaign]);

	useEffect(() => {
		initializeForm();
	}, [initializeForm]);

	const isDirty =
		fundraisingGoal !== initialFundraisingGoal ||
		budgetTarget !== initialBudgetTarget ||
		winNumber !== initialWinNumber;

	const handleReset = () => {
		setFundraisingGoal(initialFundraisingGoal);
		setBudgetTarget(initialBudgetTarget);
		setWinNumber(initialWinNumber);
	};

	const handleSave = async () => {
		if (!campaignId) {
			toast.error("No campaign found. Please try again.");
			return;
		}

		setSaving(true);
		try {
			const payload = {
				fundraising_goal: dollarsToCents(fundraisingGoal),
				budget_target: dollarsToCents(budgetTarget),
				win_number: toIntOrNull(winNumber),
			};

			await apiService.updateCampaignGoals(campaignId, payload);

			// Invalidate both campaign and dashboard queries so data refreshes
			queryClient.invalidateQueries({ queryKey: ["campaign"] });
			queryClient.invalidateQueries({
				queryKey: ["dashboard", campaignId],
			});

			// Update initial values to the new saved state
			setInitialFundraisingGoal(fundraisingGoal);
			setInitialBudgetTarget(budgetTarget);
			setInitialWinNumber(winNumber);

			toast.success("Campaign goals updated");
		} catch (error: unknown) {
			const message =
				error instanceof Error ? error.message : "Unknown error";
			toast.error(`Failed to update goals: ${message}`);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Card data-testid="campaign-goals-section">
			<CardHeader>
				<CardTitle>Campaign Goals</CardTitle>
				<CardDescription>
					Set targets for your campaign dashboard. These goals are used
					to track fundraising progress, budget allocation, and
					electoral targets.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-6">
				<div className="space-y-2">
					<Label htmlFor="fundraising-goal">Fundraising Goal</Label>
					<div className="relative">
						<span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
							$
						</span>
						<Input
							id="fundraising-goal"
							type="number"
							min="0"
							step="1"
							placeholder="50000"
							className="pl-7"
disabled={!isReadWrite}
														value={fundraisingGoal}
							onChange={(e) =>
								setFundraisingGoal(e.target.value)
							}
						/>
					</div>
					<p className="text-xs text-muted-foreground">
						Total fundraising target for your campaign (in dollars)
					</p>
				</div>

				<div className="space-y-2">
					<Label htmlFor="budget-target">Budget Target</Label>
					<div className="relative">
						<span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
							$
						</span>
						<Input
							id="budget-target"
							type="number"
							min="0"
							step="1"
							placeholder="75000"
							className="pl-7"
disabled={!isReadWrite}
														value={budgetTarget}
							onChange={(e) => setBudgetTarget(e.target.value)}
						/>
					</div>
					<p className="text-xs text-muted-foreground">
						Total campaign budget target (in dollars)
					</p>
				</div>

				<div className="space-y-2">
					<Label htmlFor="win-number">Win Number (Votes)</Label>
					<Input
						id="win-number"
						type="number"
						min="0"
						step="1"
						placeholder="5000"
disabled={!isReadWrite}
												value={winNumber}
						onChange={(e) => setWinNumber(e.target.value)}
					/>
					<p className="text-xs text-muted-foreground">
						Estimated number of votes needed to win your election.
						Used to calculate cost-per-vote on the dashboard.
					</p>
				</div>
			</CardContent>
			<CardFooter className="flex items-center gap-3 border-t pt-6">
				<Button onClick={handleSave} disabled={saving || !isDirty || !isReadWrite}>
					{saving && (
						<Loader2 className="mr-2 size-4 animate-spin" />
					)}
					{saving ? "Saving..." : "Save Goals"}
				</Button>
				{isDirty && (
					<Button variant="ghost" onClick={handleReset}>
						Cancel
					</Button>
				)}
			</CardFooter>
		</Card>
	);
}
